import { DreamTableFilterObject } from "../dreamTableTypes";

/**
 * Whether a filter value should be treated as "in use".
 *
 * Presence of the key on the filter object is NOT a usable proxy: the string
 * filter and the built-in search box both write `""` back, which survives the
 * `undefined`/`[]` stripping in `DreamTableFilters.handleSetValue`. Truthiness
 * is not usable either — `false` and `0` are legitimately populated values.
 */
export const isDreamTableFilterValueActive = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  // Booleans (including `false`) and numbers (including `0`) are populated.
  return true;
};

/**
 * How many of the given filters currently hold a populated value.
 *
 * Scoped to ids the caller passes in (i.e. the ids that have an accordion), so
 * a `searchFilterKey` without a matching filter config is not counted — its
 * value is already visible in the always-on search box above the table. Tables
 * where the search key *is* also a filter config (`CustomerListPage`,
 * `ProviderTableByType`) do count it, which is correct: that accordion really
 * is populated.
 */
export const countActiveDreamTableFilters = (
  filterIds: string[],
  filterObject: DreamTableFilterObject
): number => filterIds.filter((id) => isDreamTableFilterValueActive(filterObject[id])).length;

/**
 * Text shown on a collapsed filter's status chip, or `undefined` for no chip.
 *
 * Prefers the filter's own `describeValue` (which knows its friendly labels),
 * then falls back to a count for multi-selects and a generic marker otherwise.
 */
export const getDreamTableFilterSummary = (
  value: unknown,
  describeValue?: (value: unknown) => string | undefined
): string | undefined => {
  if (!isDreamTableFilterValueActive(value)) return undefined;

  const described = describeValue?.(value);
  if (described) return described;

  if (Array.isArray(value)) return `${value.length} selected`;

  return "Active";
};

/**
 * Shared `describeValue` for the enum-backed filters. Handles both the
 * multi-select (`string[]`) and single-select (`string`) value shapes.
 *
 * A single selection resolves to its friendly label; several collapse to a
 * count, because the side filter panel is only 15rem wide and some tables
 * default to large selections (e.g. `EnrollmentItemsConnectionTable` seeds
 * ~8 statuses).
 */
export const describeEnumSelection = (
  enumToFriendlyMap: Partial<Record<string, string>>,
  value: unknown
): string | undefined => {
  if (Array.isArray(value)) {
    if (value.length === 0) return undefined;
    if (value.length === 1) return friendlyOrRaw(enumToFriendlyMap, value[0]);
    return `${value.length} selected`;
  }

  if (typeof value === "string" && value.trim()) {
    return friendlyOrRaw(enumToFriendlyMap, value);
  }

  return undefined;
};

const friendlyOrRaw = (
  enumToFriendlyMap: Partial<Record<string, string>>,
  value: unknown
): string => {
  const key = String(value);
  return enumToFriendlyMap[key] ?? key;
};
